import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input } from '@angular/core';
import { ObserveHeightAsVarDirective } from '@modules/shared/directives/observe-height-as-var.directive';
import { type ModuleConfigState, type ModuleId, type ModuleResult } from '@pixacare/forms';
import { TuiTable } from '@taiga-ui/addon-table';
import { ModuleResolverInputs } from '../../models/module-resolver-inputs';
import { provideFormDateValueTransformer } from '../../utils/form-date-value-transformer.provider';
import { FormAutoTriggerComponent } from '../form-auto-trigger/form-auto-trigger.component';
import { EMPTY_AUTO_MODULES } from './form-grid.config';
import { FormQuestionLabelComponent } from '../form-question/form-question-label/form-question-label.component';
import { FormGridCellDirective } from './form-grid-cell/form-grid-cell.directive';
import { FormGridGroupHeaderCellComponent } from './form-grid-group-header-cell/form-grid-group-header-cell.component';
import { FormGridQuestionCellComponent } from './form-grid-question-cell/form-grid-question-cell.component';
import { FormGridSectionCellComponent } from './form-grid-section-cell/form-grid-section-cell.component';
import { FormGridSectionCellDirective } from './form-grid-section-cell/form-grid-section-cell.directive';
import { FormGridConfig } from './models/form-grid-config';
import { FormGridEntityKey } from './models/form-grid-entity-key';
import { FormGridZIndexService } from './services/form-grid-z-index.service';
import { FormGridService } from './services/form-grid.service';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({
  selector: 'pxc-form-grid',
  templateUrl: './form-grid.component.html',
  styleUrl: './form-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'isolate flex h-full min-w-max flex-col',
  },
  providers: [FormGridZIndexService, provideFormDateValueTransformer()],
  imports: [
    NgTemplateOutlet,
    FormAutoTriggerComponent,
    FormGridCellDirective,
    FormGridGroupHeaderCellComponent,
    FormGridQuestionCellComponent,
    FormGridSectionCellDirective,
    FormGridSectionCellComponent,
    TuiTable,
    FormQuestionLabelComponent,
    ObserveHeightAsVarDirective,
  ],
})
export class FormGridComponent {
  readonly config = input.required<FormGridConfig<string, any, any>>();

  protected readonly formGridService = inject(FormGridService);
  protected readonly zIndexService = inject(FormGridZIndexService);

  constructor() {
    this.zIndexService.bind(this.formGridService.groupedSections);
    effect(() => {
      const config = this.config() as FormGridConfig<string, FormGridEntityKey, unknown>;
      this.formGridService.initialize(config);
    });
  }

  autoModules(entityKey: string, type: string): ModuleConfigState[] {
    return this.formGridService.mode(entityKey, type) === 'readonly'
      ? EMPTY_AUTO_MODULES
      : this.formGridService.autoModules(entityKey, type);
  }

  moduleInputs(entityKey: string, type: string): ModuleResolverInputs {
    return this.formGridService.moduleInputs(entityKey, type)();
  }

  applyModule(
    entityKey: string,
    type: string,
    event: { moduleId: ModuleId; targetId: string; result: ModuleResult },
  ): void {
    this.formGridService.applyModuleResult(entityKey, type, event.moduleId, event.targetId, event.result);
  }
}
